import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Stack,
    Slider,
    InputAdornment,
} from '@mui/material';
import { useFixedRoutes } from '../../../context/FixedRoutesContext';
import { AppContext, Line } from '../../../context/context';
import { useEditLayer } from '../../layer_panel/functions/layerFunctions';
import type { FixedRoute } from '../../../types/fixedRoutes';
import { generateRouteColor } from '../../../utils/fixedRoutes/routeToLineConverter';

interface RouteStyleDialogProps {
    open: boolean;
    onClose: () => void;
    routeId: string | null;
}

const colorPalette = [
    '#FF0000', // Red
    '#FF4500', // Orange Red
    '#FFA500', // Orange
    '#FFD700', // Gold
    '#FFFF00', // Yellow
    '#32CD32', // Lime Green
    '#008000', // Green
    '#00CED1', // Dark Turquoise
    '#0000FF', // Blue
    '#4B0082', // Indigo
    '#800080', // Purple
    '#FF1493', // Deep Pink
    '#A52A2A', // Brown
    '#000000', // Black
    '#808080', // Gray
];

export default function RouteStyleDialog({
    open,
    onClose,
    routeId
}: RouteStyleDialogProps) {
    const { t } = useTranslation(['mapDialogs', 'common']);
    const { routes, updateRoute } = useFixedRoutes();
    const context = useContext(AppContext);
    const editLayer = useEditLayer();
    const [color, setColor] = useState<string>('#0000FF');
    const [width, setWidth] = useState<number>(6);

    const route = routeId ? routes.find(r => r.id === routeId) : null;

    useEffect(() => {
        if (route) {
            setColor(route.color || generateRouteColor(route.id));
            setWidth(route.width ?? 6);
        }
    }, [route]);

    if (!route || !context) return null;

    const handleSave = () => {
        // Update the line directly in the layer for instant visual feedback
        // This bypasses the sync effect and updates immediately, just like polygon color changes
        const { global } = context;
        
        // Search ALL layers for the route line (it might be in Fixed Routes or another layer)
        let foundLayer = null;
        let existingLine: Line | undefined = undefined;
        
        for (const layer of global.mapLayers) {
            const line = layer.shapes.find((shape) => {
                if (shape.type === 'line') {
                    const lineShape = shape as Line;
                    const metadata = (lineShape as any).fixedRouteMetadata;
                    return metadata && metadata.fixedRouteId === route.id;
                }
                return false;
            }) as Line | undefined;
            
            if (line) {
                existingLine = line;
                foundLayer = layer;
                break;
            }
        }

        if (foundLayer && existingLine) {
            // Update the line directly in the layer - instant visual update
            const updatedLine: Line = {
                ...existingLine,
                colour: color,
                borderColour: color,
                borderWidth: width,
            };

            const updatedShapes = foundLayer.shapes.map((shape) =>
                shape.id === existingLine!.id ? updatedLine : shape
            );

            const updatedLayer = {
                ...foundLayer,
                shapes: updatedShapes,
            };

            editLayer(updatedLayer, foundLayer.layerId);
        }

        // Update the route in context (this may trigger sync, but line is already updated)
        const updatedRoute: FixedRoute = {
            ...route,
            color,
            width,
        };
        updateRoute(updatedRoute);
        onClose();
    };

    const handleWidthChange = (newWidth: number) => {
        if (newWidth >= 1 && newWidth <= 20) {
            setWidth(newWidth);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('mapDialogs:routeStyle.title', { name: route.name })}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:routeStyle.routeColor')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {colorPalette.map((paletteColor) => (
                                <Box
                                    key={paletteColor}
                                    onClick={() => setColor(paletteColor)}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        backgroundColor: paletteColor,
                                        border: color === paletteColor ? '3px solid #1976d2' : '2px solid #e0e0e0',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.8,
                                            transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s',
                                    }}
                                />
                            ))}
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" gutterBottom display="block">
                                {t('mapDialogs:routeStyle.customColor')}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        backgroundColor: color,
                                        border: '2px solid #e0e0e0',
                                        borderRadius: 1,
                                        boxShadow: 1,
                                    }}
                                />
                                <input
                                    type="color"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    style={{ 
                                        width: '100%', 
                                        height: '40px',
                                        cursor: 'pointer',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                    }}
                                />
                            </Stack>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:routeStyle.lineWidth')}
                        </Typography>
                        <Stack spacing={2} direction="row" alignItems="center">
                            <Slider
                                value={width}
                                onChange={(_, value) => handleWidthChange(value as number)}
                                min={1}
                                max={20}
                                step={1}
                                sx={{ flexGrow: 1 }}
                            />
                            <TextField
                                value={width}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    if (!isNaN(value)) {
                                        handleWidthChange(value);
                                    }
                                }}
                                type="number"
                                InputProps={{
                                    inputProps: { min: 1, max: 20 },
                                    endAdornment: <InputAdornment position="end">px</InputAdornment>,
                                }}
                                sx={{ width: 100 }}
                                size="small"
                            />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {t('mapDialogs:routeStyle.lineWidthHint')}
                        </Typography>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common:cancel')}</Button>
                <Button onClick={handleSave} variant="contained">
                    {t('mapDialogs:common.saveChanges')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

